import React, { useState, useEffect, useCallback } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import {
 Card, Table, Badge, Button, Form, Modal, Row, Col, Spinner
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import AppPagination from "../Common/AppPagination";
import PermissionWrapper from "../PermissionWrapper";
import api from "../../../api";
import { getNCRList, closeNCR } from "../../../services/qcService";

const STATUS_COLORS = {
 Open: "danger",
 Closed: "success",
};

const actionBtnStyle = {
 minWidth: 34,
 width: 34,
 height: 34,
 borderRadius: 12,
 boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
 padding: 0,
};

const ActionButton = ({ variant, icon, label, onClick, title, disabled = false }) => (
 <Button
 type="button"
 size="sm"
 variant={variant}
 title={title || label}
 onClick={onClick}
 disabled={disabled}
 className="d-flex align-items-center justify-content-center"
 style={actionBtnStyle}
 >
 <i className={`fas ${icon}`} style={{ fontSize: 13, lineHeight: 1 }} />
 </Button>
);

const getPrimaryNcrReport = (record) =>
 (record?.ncrReports || []).find(
 (report) => report?.details || report?.correctiveAction || report?.remarks
 ) || record?.ncrReports?.[0] || null;

/* Helper: NCR closure status */
const ncrStatus = (rec) => {
 const isClosed =
 rec?.status === "Closed" ||
 !!rec?.ncrClosedDate ||
 (Array.isArray(rec?.ncrReports) && rec.ncrReports.some((report) => !!report?.closedDate));
 return isClosed ? "Closed" : "Open";
};

const getClosedDate = (rec) => {
 const reportDate = rec?.ncrReports?.find((report) => report?.closedDate)?.closedDate;
 return reportDate || rec?.ncrClosedDate || null;
};

/* CloseNCRModal removed: close is handled directly from the list action */

/* Raise Ticket from NCR Modal */
const RaiseTicketModal = ({ show, record, onHide, onSaved }) => {
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [users, setUsers] = useState([]);
 const [form, setForm] = useState({
 project: "", workOrder: "", priority: "High",
 title: "", description: "", assignedTo: "",
 });
 const [saving, setSaving] = useState(false);

 useEffect(() => {
 if (!show) return;
 const load = async () => {
 try {
 const [pRes, uRes] = await Promise.all([
 api.get("/projects?limit=500"),
 api.get("/users?limit=500"),
 ]);
 setProjects(pRes.data?.data || []);
 const allUsers = uRes.data?.data || [];
 setUsers(allUsers.filter((u) => (u.role?.name || "").toLowerCase() !== "client"));
 } catch (_) {}
 };
 load();
 }, [show]);

 useEffect(() => {
 if (record) {
 setForm({
 project: record.project?._id || record.project || "",
 workOrder: record.workOrder?._id || record.workOrder || "",
 priority: "High",
 title: `NCR: ${record.activityOrScope || record.qcNumber}`,
 description: record.nonConformanceDetails || "",
 assignedTo: "",
 });
 }
 }, [record]);

 useEffect(() => {
 if (!form.project) { setWorkOrders([]); return; }
 api.get("/work-orders", { params: { project: form.project, limit: 500 } })
 .then((r) => setWorkOrders(r.data?.data || []))
 .catch(() => {});
 }, [form.project]);

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!form.project || !form.description.trim()) {
 toast.error("Project and description are required");
 return;
 }
 setSaving(true);
 try {
 const data = new FormData();
 data.append("project", form.project);
 if (form.workOrder) data.append("workOrder", form.workOrder);
 data.append("category", "Non-Conformance (NCR)");
 data.append("priority", form.priority);
 data.append("title", form.title);
 data.append("description", form.description);
 if (form.assignedTo) data.append("assignedTo", form.assignedTo);
 data.append("linkedQC", record._id);
 await api.post("/client-tickets", data, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 toast.success("NCR ticket raised successfully");
 onSaved && onSaved();
 onHide();
 } catch (err) {
 toast.error(err?.response?.data?.message || "Failed to raise ticket");
 } finally {
 setSaving(false);
 }
 };

 if (!record) return null;
 return (
 <Modal show={show} onHide={onHide} centered size="lg">
 <Modal.Header closeButton>
 <Modal.Title>
 <i className="fas fa-ticket-alt text-primary me-2" />
 Raise NCR Ticket - {record.qcNumber}
 </Modal.Title>
 </Modal.Header>
 <Form onSubmit={handleSubmit}>
 <Modal.Body>
 <div className="alert alert-info py-2 mb-3" style={{ fontSize: "0.85rem" }}>
 <i className="fas fa-link me-1" />
 Ticket will be linked to QC Inspection <strong>{record.qcNumber}</strong>
 </div>
 <Row className="g-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label className="fw-semibold">Project <span className="text-danger">*</span></Form.Label>
 <Form.Select
 value={form.project}
 onChange={(e) => setForm((p) => ({ ...p, project: e.target.value, workOrder: "" }))}
 required
 >
 <option value="">Select Project</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>{p.projectName || p.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label className="fw-semibold">Work Order</Form.Label>
 <Form.Select
 value={form.workOrder}
 onChange={(e) => setForm((p) => ({ ...p, workOrder: e.target.value }))}
 >
 <option value="">Optional</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>{wo.woNo}{wo.label ? ` – ${wo.label}` : ""}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label className="fw-semibold">Priority</Form.Label>
 <Form.Select
 value={form.priority}
 onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
 >
 {["Low", "Medium", "High", "Critical"].map((v) => (
 <option key={v} value={v}>{v}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label className="fw-semibold">Assign To</Form.Label>
 <Form.Select
 value={form.assignedTo}
 onChange={(e) => setForm((p) => ({ ...p, assignedTo: e.target.value }))}
 >
 <option value="">Auto / Assign Later</option>
 {users.map((u) => (
 <option key={u._id} value={u._id}>{u.name}</option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={12}>
 <Form.Group>
 <Form.Label className="fw-semibold">Title</Form.Label>
 <Form.Control
 value={form.title}
 onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
 placeholder="Brief summary"
 />
 </Form.Group>
 </Col>
 <Col md={12}>
 <Form.Group>
 <Form.Label className="fw-semibold">Description <span className="text-danger">*</span></Form.Label>
 <Form.Control
 as="textarea"
 rows={4}
 value={form.description}
 onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
 placeholder="Describe the non-conformance in detail..."
 required
 />
 </Form.Group>
 </Col>
 </Row>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="outline-secondary" onClick={onHide} type="button">Cancel</Button>
 <Button variant="primary" type="submit" disabled={saving}>
 {saving ? <Spinner size="sm" className="me-1" /> : <i className="fas fa-ticket-alt me-1" />}
 Raise Ticket
 </Button>
 </Modal.Footer>
 </Form>
 </Modal>
 );
};

/* 
 MAIN NCR MONITOR COMPONENT
 */
const NCRMonitor = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const navigate = useNavigate();

 const [records, setRecords] = useState([]);
 const [loading, setLoading] = useState(true);
 const [pagination, setPagination] = useState({
 currentPage: 1, totalPages: 1, totalCount: 0, limit: 15,
 });
 const [filters, setFilters] = usePersistentFilters("qc_ncr_filters", {
 closed: "", search: "", workOrder: "",
 });
 const [workOrders, setWorkOrders] = useState([]);

 const [ticketModal, setTicketModal] = useState({ show: false, record: null });

 useEffect(() => {
 api.get("/work-orders?limit=500")
 .then((r) => setWorkOrders(r.data?.data || []))
 .catch(() => {});
 }, []);

 const fetchNCRs = useCallback(async () => {
 setLoading(true);
 try {
 const params = {
 page: pagination.currentPage,
 limit: pagination.limit,
 ...(filters.closed && { closed: filters.closed }),
 ...(filters.search && { search: filters.search }),
 ...(filters.workOrder && { workOrder: filters.workOrder }),
 };
 const res = await getNCRList(params);
 if (res.data.success) {
 setRecords(res.data.data);
 setPagination((p) => ({
 ...p,
 totalPages: res.data.pagination.totalPages,
 totalCount: res.data.pagination.totalCount,
 currentPage: res.data.pagination.currentPage,
 }));
 }
 } catch (_) {
 toast.error("Failed to load NCR list");
 } finally {
 setLoading(false);
 }
 }, [pagination.currentPage, pagination.limit, filters]);

 useEffect(() => { fetchNCRs(); }, [fetchNCRs]);

 const handleFilterChange = (name, value) => {
 setFilters((p) => ({ ...p, [name]: value }));
 setPagination((p) => ({ ...p, currentPage: 1 }));
 };

 const handleEdit = (record) => {
 navigate(`/qc/edit/${record._id}`);
 };

 const hasSavedCloseImages = (record) =>
 Array.isArray(record?.ncrReports) &&
 record.ncrReports.some(
 (report) => Array.isArray(report?.closePhotos) && report.closePhotos.length > 0
 );

 const handleCloseNcr = async (record) => {
 if (!hasSavedCloseImages(record)) {
 swal({
 title: "Warning",
 text: "Please upload closing images first.",
 icon: "warning",
 button: "OK",
 });
 return;
 }

 try {
 await closeNCR(record._id, new FormData());
 toast.success("NCR closed successfully");
 fetchNCRs();
 } catch (err) {
 toast.error(err?.response?.data?.message || "Failed to close NCR");
 }
 };

 // Summary counts
 const openCount = records.filter((r) => ncrStatus(r) === "Open").length;
 const closedCount = records.filter((r) => ncrStatus(r) === "Closed").length;

 return (
 <div>
 {/* Summary strips */}
 <Row className="g-3 mb-3">
 {[
 { label: "Total NCRs", value: pagination.totalCount, color: "dark", icon: "fa-exclamation-circle" },
 { label: "Open", value: filters.closed === "" ? "-" : openCount, color: "danger", icon: "fa-dot-circle" },
 { label: "Closed", value: filters.closed === "" ? "-" : closedCount, color: "success", icon: "fa-check-circle" },
 ].map((s) => (
 <Col key={s.label} xs={4}>
 <Card className={`bg-${s.color} text-white shadow-sm border-0`}>
 <Card.Body className="d-flex align-items-center p-3 gap-3">
 <i className={`fas ${s.icon} fa-2x opacity-50`} />
 <div>
 <p className="mb-0 opacity-75" style={{ fontSize: "0.75rem" }}>{s.label}</p>
 <h4 className="mb-0 fw-bold">{s.value}</h4>
 </div>
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* Filters */}
 <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
 <div style={{ width: 190 }}>
 <Form.Control
 size="sm"
 placeholder="Search QC No / scope..."
 value={filters.search}
 onChange={(e) => handleFilterChange("search", e.target.value)}
 />
 </div>
 <div style={{ width: 150 }}>
 <Form.Select
 size="sm"
 value={filters.closed}
 onChange={(e) => handleFilterChange("closed", e.target.value)}
 >
 <option value="">All NCRs</option>
 <option value="false">Open Only</option>
 <option value="true">Closed Only</option>
 </Form.Select>
 </div>
 <div style={{ width: 200 }}>
 <Form.Select
 size="sm"
 value={filters.workOrder}
 onChange={(e) => handleFilterChange("workOrder", e.target.value)}
 >
 <option value="">All Work Orders</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </div>
 </div>

 {/* Table */}
 <Card className="shadow-sm border-0">
 <Card.Body className="p-0">
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 </div>
 ) : records.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-check-double fa-3x mb-3 d-block text-success" />
 <h6>No NCRs found</h6>
 <p className="small">No non-conformance records match the selected filters.</p>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover size="sm" className="align-middle mb-0">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>QC No</th>
 <th>Date</th>
 <th>Activity / Scope</th>
 <th>Work Order</th>
 <th>Non-Conformance</th>
 <th>Corrective Action</th>
 <th>Linked Ticket</th>
 <th>NCR Status</th>
 <th>Closed On</th>
 <th style={{ width: 160 }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {records.map((rec, i) => {
 const isClosed = ncrStatus(rec) === "Closed";
 const hasTicket = !!rec.linkedTicket;
 return (
 <tr key={rec._id}>
 <td>{(pagination.currentPage - 1) * pagination.limit + i + 1}</td>
 <td>
 <button
 className="btn btn-link btn-sm p-0 text-primary fw-semibold"
 onClick={() => navigate(`/qc/edit/${rec._id}`)}
 >
 {rec.qcNumber}
 </button>
 </td>
 <td className="text-nowrap">
 {new Date(rec.inspectionDate).toLocaleDateString("en-IN")}
 </td>
 <td>
 <div className="fw-semibold small">{rec.activityOrScope}</div>
 {rec.location && (
 <small className="text-muted">
 <i className="fas fa-map-marker-alt me-1" />{rec.location}
 </small>
 )}
 </td>
 <td className="small text-primary">{rec.workOrder?.woNo || "-"}</td>
 <td>
 <div className="small" style={{ maxWidth: 200 }}>
 {rec.ncrReports?.length
 ? getPrimaryNcrReport(rec)?.details || "-"
 : rec.nonConformanceDetails || "-"}
 {rec.ncrReports?.length > 1 && (
 <span className="ms-1 badge bg-light text-dark border">
 +{rec.ncrReports.length - 1}
 </span>
 )}
 </div>
 </td>
 <td>
 <div className="small text-muted" style={{ maxWidth: 180 }}>
 {rec.ncrReports?.length
 ? getPrimaryNcrReport(rec)?.correctiveAction || (isClosed ? "-" : <em className="text-danger">Pending</em>)
 : rec.correctiveAction || (isClosed ? "-" : <em className="text-danger">Pending</em>)}
 </div>
 </td>
 <td>
 {hasTicket ? (
 <span className="badge bg-info text-dark">
 <i className="fas fa-ticket-alt me-1" />
 {rec.linkedTicket.ticketNo}
 </span>
 ) : (
 <span className="text-muted small">-</span>
 )}
 </td>
 <td>
 <Badge bg={isClosed ? "success" : "danger"}>
 {isClosed ? "Closed" : "Open"}
 </Badge>
 </td>
 <td className="text-nowrap small">
 {isClosed
 ? new Date(getClosedDate(rec) || Date.now()).toLocaleDateString("en-IN")
 : <span className="text-muted">-</span>}
 </td>
 <td>
 <div className="d-flex align-items-center gap-1 flex-nowrap">
 <ActionButton
 variant="outline-primary"
 icon="fa-ticket-alt"
 label="Ticket"
 title="Raise NCR Ticket"
 onClick={() => setTicketModal({ show: true, record: rec })}
 />
 <ActionButton
 variant="outline-secondary"
 icon="fa-eye"
 label="View"
 title="View QC"
 onClick={() => navigate(`/qc/edit/${rec._id}`)}
 />
 <ActionButton
 variant="outline-warning"
 icon="fa-edit"
 label="Edit"
 title="Edit QC"
 onClick={() => handleEdit(rec)}
 />
 {!isClosed ? (
 <PermissionWrapper
 user={user}
 required={["edit:qc"]}
 mode="some"
 >
 <ActionButton
 variant="outline-success"
 icon="fa-check"
 label="Close"
 title="Close NCR"
 onClick={() => handleCloseNcr(rec)}
 />
 </PermissionWrapper>
 ) : (
 <ActionButton
 variant="success"
 icon="fa-check-circle"
 label="Closed"
 title="NCR Closed"
 disabled
 />
 )}
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 {!loading && records.length > 0 && (
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={records.length}
 limit={pagination.limit}
 onPageChange={(page) => setPagination((p) => ({ ...p, currentPage: page }))}
 />
 )}
 </Card>

 <RaiseTicketModal
 show={ticketModal.show}
 record={ticketModal.record}
 onHide={() => setTicketModal({ show: false, record: null })}
 onSaved={fetchNCRs}
 />
 </div>
 );
};

export default NCRMonitor;
